import { ScanNotFound } from "@hobbyshopify/backend-client";
import { hasError } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import { loadScan } from "../../core/ops/scan-ops";
import { ScanEditorController, type ScanEditorDeps } from "../../sections/scan-editor/scan-editor-controller";
import { EditScanActions, type EditScanKind, type EditScanMode, EditScanState } from "./edit-scan-model";

export type { EditScanKind, EditScanMode };

export type EditScanDeps = ScanEditorDeps;

export interface EditScanParams {
  readonly scanId: string;
  readonly kind: EditScanKind;
  readonly mode: EditScanMode;
}

export interface EditScanRouting {
  /** Where confirm, reject and cancel go: the list the scan was opened from. */
  back(): void;
}

/**
 * Review screen for one scan: both reviewing a customer's pending scan and
 * re-opening a confirmed one land here. The scan is fetched by id so the page
 * survives refresh and direct navigation; the editing itself is the shared
 * `ScanEditorController` section.
 */
export class EditScanController extends SubtreeController implements EditScanActions {
  readonly state: EditScanState;
  readonly editor: ScanEditorController;
  #started = false;

  constructor(
    readonly params: EditScanParams,
    readonly deps: EditScanDeps,
    readonly routing: EditScanRouting,
  ) {
    super();
    this.state = new EditScanState(params.kind, params.mode);
    this.subtree.put(EditScanState, this.state);
    this.subtree.put(EditScanActions, this);
    this.editor = this.own(
      new ScanEditorController(this.subtree, deps, {
        onConfirmed: () => this.routing.back(),
        onRejected: () => this.routing.back(),
      }),
    );

    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      if (shopId !== null && !this.#started) {
        this.#started = true;
        void this.#start(shopId);
      }
    }, [deps.storeRepository]);
  }

  cancel = () => {
    this.routing.back();
  };

  retry = () => {
    const shopId = this.deps.storeRepository.shopId;
    if (shopId === null || this.state.status.value === "loading") return;
    this.state.status.value = "loading";
    void this.#start(shopId);
  };

  async #start(shopId: string): Promise<void> {
    const r = await loadScan({ ...this.deps, shopId, scanId: this.params.scanId });
    if (this.isDisposed) return;
    if (!r.ok) {
      this.state.status.value = hasError(r, ScanNotFound) ? "notFound" : "error";
      return;
    }
    if (r.value.recognitionOutcome === "no_match") {
      this.state.noMatch.value = r.value;
      this.state.status.value = "ready";
      return;
    }
    this.editor.seed(r.value);
    this.state.status.value = "ready";
  }
}
